import { Text } from '@/components/custom-components'
import { Loading } from '@/components/loading.tsx'
import { Button } from '@/components/shadcn/button.tsx'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/shadcn/dialog.tsx'
import { Input } from '@/components/shadcn/input.tsx'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form.tsx'
import showToastNotification from '@/hooks/useShowToast.tsx'
import { trpc } from '@/utils/trpc.ts'
import { zodResolver } from '@hookform/resolvers/zod'
import { CheckCircle2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

export const CreateTeamDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: () => void
}) => {
  const utils = trpc.useUtils()
  const [message, setMessage] = useState<string | null>(null)
  const { mutate: createTeam, isPending } = trpc.createTeam.useMutation({
    onSuccess: async () => {
      setMessage('Team created successfully')
      await utils.getUserTeams.invalidate()
      setTimeout(() => {
        onOpenChange()
        setMessage(null)
      }, 1500)
    },
    onError: (error) => {
      showToastNotification('error', {
        message: `Error creating team.`,
        description: `${error.message}`,
      })
    },
  })

  const formSchema = z.object({
    name: z.string().min(1, 'Team name is required'),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: '' },
  })

  const handleSubmit = (values: z.infer<typeof formSchema>) => {
    createTeam(values)
  }

  return (
    <Dialog open={open || isPending} onOpenChange={onOpenChange}>
      <DialogContent
        className="gap-6 bg-themebackground dark:bg-themebackgrounddark"
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.preventDefault()
        }}
      >
        <DialogHeader>
          <DialogTitle>
            <Text weight="semibold" size="lg" className="leading-7">
              {isPending ? 'Creating Team' : 'Create Team'}
            </Text>
          </DialogTitle>
        </DialogHeader>
        {message ? (
          <div className="flex flex-col items-center text-center gap-2 w-full h-full m-auto">
            <CheckCircle2 size="3rem" strokeWidth="1.2" color="green" />
            <p>{message}</p>
          </div>
        ) : isPending ? (
          <div className="flex flex-col gap-3 w-full h-full m-auto">
            <Loading />
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <div className="flex flex-col gap-6">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-medium leading-5">Team Name</FormLabel>
                      <FormControl>
                        <Input className="shadow-none" placeholder="Team name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-end gap-2">
                  <Button className="w-15 px-3 h-8 text-sm" variant="outline" onClick={() => onOpenChange()}>
                    Cancel
                  </Button>
                  <Button type="submit" className="w-15 px-3 h-8 text-sm" variant="default">
                    {isPending ? 'Submitting...' : 'Save'}
                  </Button>
                </div>
              </div>
            </form>
          </Form>
        )}
      </DialogContent>
    </Dialog>
  )
}
